import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import '@/styles/Board/TiptapTextEditor.less';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import Color from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import Strike from '@tiptap/extension-strike';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table/cell';
import { TableHeader } from '@tiptap/extension-table/header';
import { TableRow } from '@tiptap/extension-table/row';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { Markdown } from '@tiptap/markdown';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { EDITOR_CONSTANTS, TEXT_ELEMENT_CONSTANTS } from './constants';
import TableToolbar from './TableToolbar';
import TiptapToolbar from './TiptapToolbar';
const { DEFAULT_FONT_FAMILY, DEFAULT_FONT_SIZE, DEFAULT_ELEMENT_WIDTH, DEFAULT_ELEMENT_HEIGHT, BASE_HEIGHT_FOR_TEXT_SCALE, } = TEXT_ELEMENT_CONSTANTS;
const { TOOLBAR_HEIGHT, BUTTONS_HEIGHT, TOOLBAR_OFFSET, BUTTONS_OFFSET, TOOLBAR_PADDING, TOOLBAR_BORDER_RADIUS, TOOLBAR_BACKGROUND_COLOR, TOOLBAR_Z_INDEX_BASE, TOOLBAR_Z_INDEX_TABLE, } = EDITOR_CONSTANTS;
// ==================== INTERNAL EDITOR COMPONENT ====================
/**
 * Внутренний компонент редактора Tiptap
 * Обрабатывает Markdown контент и переключение между форматированным и source режимами
 *
 * @remarks
 * Компонент использует debounce для обновления контента (300ms задержка)
 * Поддерживает два режима отображения:
 * - Форматированный режим: отображает EditorContent с визуальным форматированием
 * - Source режим: отображает textarea с исходным Markdown кодом
 */
const EditorCore = forwardRef(({ content, onUpdate, editable = true, className, style, showMarkdownSource = false, placeholder, }, ref) => {
    // Debounce для onUpdate, чтобы не вызывать обновление при каждом символе
    const updateTimeoutRef = useRef(null);
    const debouncedOnUpdate = useCallback((markdown) => {
        if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
        }
        updateTimeoutRef.current = setTimeout(() => {
            onUpdate === null || onUpdate === void 0 ? void 0 : onUpdate(markdown);
        }, 300); // 300ms задержка для debounce
    }, [onUpdate]);
    // Очистка timeout при размонтировании
    useEffect(() => {
        return () => {
            if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
            }
        };
    }, []);
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
                codeBlock: false,
                code: false,
                strike: false,
                link: false,
                underline: false,
            }),
            Code,
            CodeBlock.configure({
                HTMLAttributes: { class: 'tiptap-code-block' },
            }),
            Strike,
            Underline,
            TextStyle,
            Color,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: { class: 'tiptap-link' },
            }),
            Superscript,
            Subscript,
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
            Markdown.configure({
                markedOptions: {
                    breaks: false, // Стандартное Markdown: двойной Enter = новый параграф
                    gfm: true, // GitHub Flavored Markdown
                    pedantic: false, // Не быть слишком строгим к синтаксису
                },
            }),
        ],
        content,
        contentType: 'markdown',
        editable,
        /**
         * Опция для контроля немедленного рендеринга редактора
         * true - редактор рендерится сразу (поведение по умолчанию)
         */
        immediatelyRender: true,
        /**
         * Опция для контроля ре-рендеринга на каждую транзакцию
         * false - редактор НЕ ре-рендерится на каждую транзакцию, что предотвращает
         * ре-рендеры при каждом напечатанном символе и улучшает производительность
         */
        shouldRerenderOnTransaction: false,
        onUpdate: ({ editor }) => {
            var _a, _b;
            const markdown = ((_b = (_a = editor).getMarkdown) === null || _b === void 0 ? void 0 : _b.call(_a)) || '';
            debouncedOnUpdate(markdown);
        },
        editorProps: {
            attributes: {
                class: className || 'tiptap-editor',
                style: style
                    ? Object.entries(style)
                        .map(([k, v]) => {
                        const cssKey = k.replace(/([A-Z])/g, '-$1').toLowerCase();
                        return `${cssKey}: ${v}`;
                    })
                        .join('; ')
                    : 'width: 100%; height: 100%; margin: 0; padding: 0;',
            },
            handleClick: (view, pos, event) => {
                const target = event.target;
                const linkElement = target.closest('a.tiptap-link');
                if ((linkElement === null || linkElement === void 0 ? void 0 : linkElement.href) && (event.ctrlKey || event.metaKey)) {
                    event.preventDefault();
                    window.open(linkElement.href, '_blank', 'noopener,noreferrer');
                    return true;
                }
                return false;
            },
        },
    });
    const [markdownSourceContent, setMarkdownSourceContent] = useState('');
    const lastContentRef = useRef('');
    const isInitializedRef = useRef(false);
    useImperativeHandle(ref, () => ({
        getHTML: () => (editor === null || editor === void 0 ? void 0 : editor.getHTML()) || '',
        getMarkdown: () => {
            if (!editor)
                return '';
            return typeof editor.getMarkdown === 'function'
                ? editor.getMarkdown()
                : '';
        },
        getText: () => (editor === null || editor === void 0 ? void 0 : editor.getText()) || '',
        setContent: (newContent, contentType = 'markdown') => {
            if (!editor)
                return;
            const isHTML = newContent.trim().startsWith('<') && newContent.includes('>');
            if (isHTML || contentType === 'html') {
                editor.commands.setContent(newContent);
            }
            else if (contentType === 'markdown') {
                editor.commands.setContent(newContent, { contentType: 'markdown' });
            }
            else {
                editor.commands.setContent(newContent);
            }
        },
        focus: () => editor === null || editor === void 0 ? void 0 : editor.commands.focus(),
        getEditor: () => editor,
        getCurrentMarkdown: () => {
            if (showMarkdownSource && markdownSourceContent) {
                return markdownSourceContent;
            }
            return typeof editor.getMarkdown === 'function'
                ? editor.getMarkdown()
                : '';
        },
    }));
    // Синхронизация контента из props
    useEffect(() => {
        if (!editor || !content)
            return;
        if (!isInitializedRef.current) {
            lastContentRef.current = content;
            isInitializedRef.current = true;
            return;
        }
        if (lastContentRef.current === content || showMarkdownSource)
            return;
        requestAnimationFrame(() => {
            if (!editor || !content)
                return;
            const currentMarkdown = typeof editor.getMarkdown === 'function'
                ? editor.getMarkdown()
                : editor.getHTML();
            const normalizeMarkdown = (md) => {
                if (!md || typeof md !== 'string')
                    return '';
                return md.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
            };
            const normalizedCurrent = normalizeMarkdown(currentMarkdown);
            const normalizedContent = normalizeMarkdown(content);
            if (normalizedContent !== normalizedCurrent && normalizedContent.length > 0) {
                editor.commands.setContent(content, { contentType: 'markdown' });
                lastContentRef.current = content;
            }
        });
    }, [content, editor, showMarkdownSource]);
    useEffect(() => {
        if (editor) {
            editor.setEditable(editable);
        }
    }, [editable, editor]);
    // Синхронизация при переключении в source режим
    useEffect(() => {
        if (showMarkdownSource && editor) {
            requestAnimationFrame(() => {
                if (!editor)
                    return;
                const markdownContent = typeof editor.getMarkdown === 'function' ? editor.getMarkdown() : '';
                setMarkdownSourceContent(markdownContent || '');
            });
        }
    }, [showMarkdownSource, editor]);
    // Синхронизация при переключении обратно в форматированный режим
    const previousShowMarkdownSourceRef = useRef(showMarkdownSource);
    useEffect(() => {
        const wasInSourceMode = previousShowMarkdownSourceRef.current === true;
        const isNowInFormattedMode = showMarkdownSource === false;
        previousShowMarkdownSourceRef.current = showMarkdownSource;
        if (wasInSourceMode && isNowInFormattedMode && editor) {
            setTimeout(() => {
                if (!editor)
                    return;
                const sourceContent = markdownSourceContent;
                if ((sourceContent === null || sourceContent === void 0 ? void 0 : sourceContent.trim().length) > 0) {
                    editor.commands.setContent(sourceContent, { contentType: 'markdown' });
                }
            }, 0);
        }
    }, [showMarkdownSource, editor, markdownSourceContent]);
    if (!editor)
        return null;
    // Source режим - показываем textarea
    if (showMarkdownSource) {
        return (_jsx("textarea", { className: className || 'tiptap-editor', style: Object.assign(Object.assign({}, style), { width: '100%', height: '100%', border: 'none', outline: 'none', resize: 'none', fontFamily: 'monospace', padding: 0, margin: 0, boxSizing: 'border-box', backgroundColor: 'transparent', color: 'inherit', overflow: 'auto' }), value: markdownSourceContent, onChange: (e) => {
                if (editable) {
                    const newValue = e.target.value;
                    setMarkdownSourceContent(newValue);
                    if (editor) {
                        editor.commands.setContent(newValue, { contentType: 'markdown' });
                    }
                    debouncedOnUpdate(newValue);
                }
            }, readOnly: !editable, placeholder: placeholder }));
    }
    // Форматированный режим - показываем EditorContent
    return (_jsx("div", { className: className || 'tiptap-text-editor', style: Object.assign(Object.assign({}, style), { width: '100%', height: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }), children: _jsx(EditorContent, { editor: editor }) }));
});
EditorCore.displayName = 'EditorCore';
// Мемоизируем EditorCore для предотвращения лишних ре-рендеров
// Используем кастомное сравнение для правильной работы с forwardRef
export const MemoizedEditorCore = memo(EditorCore, (prevProps, nextProps) => {
    return (prevProps.content === nextProps.content &&
        prevProps.editable === nextProps.editable &&
        prevProps.showMarkdownSource === nextProps.showMarkdownSource &&
        prevProps.placeholder === nextProps.placeholder &&
        prevProps.className === nextProps.className &&
        JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style) &&
        prevProps.onUpdate === nextProps.onUpdate);
});
// ==================== MAIN COMPONENT ====================
/**
 * Встроенный редактор текста, отображаемый поверх элемента на доске
 *
 * @remarks
 * Компонент предоставляет полнофункциональный редактор текста с поддержкой:
 * - Форматирования текста (жирный, курсив, подчеркивание и т.д.)
 * - Заголовков и списков
 * - Таблиц
 * - Ссылок
 * - Настройки стилей (размер шрифта, цвета, выравнивание)
 * - Режима просмотра исходного Markdown кода
 *
 * Редактор автоматически сохраняется при клике вне области редактирования
 * или при нажатии Ctrl/Cmd + Enter. Отмена редактирования - клавиша Escape.
 *
 * @param props - Пропсы компонента
 */
const InlineTextEditor = ({ element, onSave, onCancel, stagePosition = { x: 0, y: 0 }, zoom = 1, renderMode = false, }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    // Инициализация состояния
    const initialContent = useMemo(() => {
        var _a;
        const textProp = ((_a = element.props) === null || _a === void 0 ? void 0 : _a.text) || '';
        return textProp && !textProp.startsWith('<') ? textProp : textProp || '';
    }, [(_a = element.props) === null || _a === void 0 ? void 0 : _a.text]);
    const [markdownContent, setMarkdownContent] = useState(initialContent);
    const [showMarkdownSource, setShowMarkdownSource] = useState(false);
    const [editorReady, setEditorReady] = useState(false);
    const [styles, setStyles] = useState(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return ({
            fontFamily: ((_a = element.props) === null || _a === void 0 ? void 0 : _a.fontFamily) || DEFAULT_FONT_FAMILY,
            fontSize: ((_b = element.props) === null || _b === void 0 ? void 0 : _b.fontSize) || DEFAULT_FONT_SIZE,
            fontStyle: ((_c = element.props) === null || _c === void 0 ? void 0 : _c.fontStyle) || 'normal',
            textDecoration: ((_d = element.props) === null || _d === void 0 ? void 0 : _d.textDecoration) || 'none',
            textAlign: ((_e = element.props) === null || _e === void 0 ? void 0 : _e.textAlign) || 'left',
            verticalAlign: ((_f = element.props) === null || _f === void 0 ? void 0 : _f.verticalAlign) || 'top',
            textColor: ((_g = element.props) === null || _g === void 0 ? void 0 : _g.textColor) || '#000000',
            backgroundColor: ((_h = element.props) === null || _h === void 0 ? void 0 : _h.backgroundColor) || '#ffffff38',
        });
    });
    // Refs
    const editorRef = useRef(null);
    const toolbarRef = useRef(null);
    const tableToolbarRef = useRef(null);
    const elementRef = useRef(null);
    const buttonsRef = useRef(null);
    // Вычисляем размеры и позиции
    const elementDimensions = useMemo(() => {
        var _a, _b;
        return ({
            width: ((_a = element.props) === null || _a === void 0 ? void 0 : _a.w) || DEFAULT_ELEMENT_WIDTH,
            height: ((_b = element.props) === null || _b === void 0 ? void 0 : _b.h) || DEFAULT_ELEMENT_HEIGHT,
        });
    }, [(_b = element.props) === null || _b === void 0 ? void 0 : _b.w, (_c = element.props) === null || _c === void 0 ? void 0 : _c.h]);
    /**
     * Вычисляет коэффициент масштабирования текста на основе высоты элемента.
     * Базовая высота 1000px = коэффициент 1.
     * Чем больше высота элемента, тем больше коэффициент (текст крупнее).
     * Чем меньше высота элемента, тем меньше коэффициент (текст мельче).
     */
    const textScaleFromHeight = useMemo(() => {
        return 1;
        const height = elementDimensions.height;
        return height / BASE_HEIGHT_FOR_TEXT_SCALE;
    }, [elementDimensions.height]);
    const elementPosition = useMemo(() => {
        var _a, _b;
        // В режиме рендеринга используем относительное позиционирование
        if (renderMode) {
            return {
                x: 0,
                y: 0,
                width: elementDimensions.width,
                height: elementDimensions.height,
            };
        }
        // В обычном режиме используем абсолютное позиционирование
        return {
            x: (((_a = element.props) === null || _a === void 0 ? void 0 : _a.x) || 0) * zoom + stagePosition.x,
            y: (((_b = element.props) === null || _b === void 0 ? void 0 : _b.y) || 0) * zoom + stagePosition.y,
            width: elementDimensions.width * zoom,
            height: elementDimensions.height * zoom,
        };
    }, [(_d = element.props) === null || _d === void 0 ? void 0 : _d.x, (_e = element.props) === null || _e === void 0 ? void 0 : _e.y, elementDimensions, zoom, stagePosition, renderMode]);
    const toolbarPosition = useMemo(() => ({
        x: elementPosition.x,
        y: elementPosition.y - TOOLBAR_HEIGHT * zoom - TOOLBAR_OFFSET,
        width: elementDimensions.width,
        height: TOOLBAR_HEIGHT,
    }), [elementPosition, elementDimensions.width, zoom]);
    const tableToolbarPosition = useMemo(() => ({
        x: elementPosition.x,
        y: elementPosition.y - TOOLBAR_HEIGHT * zoom * 2 - TOOLBAR_OFFSET * 2,
        width: elementDimensions.width,
        height: TOOLBAR_HEIGHT,
    }), [elementPosition, elementDimensions.width, zoom]);
    const buttonsPosition = useMemo(() => ({
        x: elementPosition.x,
        y: elementPosition.y + elementPosition.height + BUTTONS_OFFSET,
        width: elementDimensions.width,
        height: BUTTONS_HEIGHT,
    }), [elementPosition, elementDimensions.width]);
    // Обработчики событий
    const handleContentUpdate = useCallback((content) => {
        setMarkdownContent(content);
    }, []);
    const handleStyleChange = useCallback((updates) => {
        setStyles((prev) => (Object.assign(Object.assign({}, prev), updates)));
    }, []);
    const handleSave = useCallback(() => {
        var _a, _b, _c;
        const content = ((_b = (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.getCurrentMarkdown) === null || _b === void 0 ? void 0 : _b.call(_a)) ||
            ((_c = editorRef.current) === null || _c === void 0 ? void 0 : _c.getMarkdown()) ||
            markdownContent;
        onSave(element.id, content, styles);
    }, [element.id, markdownContent, styles, onSave]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSave();
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
        }
    }, [handleSave, onCancel]);
    // Эффекты
    useEffect(() => {
        var _a;
        (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    useEffect(() => {
        const checkEditor = () => {
            var _a;
            const editor = (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.getEditor();
            if (editor) {
                setEditorReady(true);
            }
            else {
                requestAnimationFrame(checkEditor);
            }
        };
        const timeoutId = setTimeout(() => requestAnimationFrame(checkEditor), 100);
        return () => clearTimeout(timeoutId);
    }, []);
    useEffect(() => {
        const handleClickOutside = (event) => {
            var _a, _b, _c, _d, _e, _f;
            const target = event.target;
            // Проверяем, не был ли клик внутри всплывающего тулбара (рендерится через портал)
            const floatingToolbar = (_b = (_a = target).closest) === null || _b === void 0 ? void 0 : _b.call(_a, '.floating-toolbar');
            const isInsideEditor = ((_c = toolbarRef.current) === null || _c === void 0 ? void 0 : _c.contains(target)) ||
                ((_d = tableToolbarRef.current) === null || _d === void 0 ? void 0 : _d.contains(target)) ||
                ((_e = elementRef.current) === null || _e === void 0 ? void 0 : _e.contains(target)) ||
                ((_f = buttonsRef.current) === null || _f === void 0 ? void 0 : _f.contains(target)) ||
                floatingToolbar !== null; // Клик внутри всплывающего тулбара
            if (!isInsideEditor) {
                handleSave();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleSave]);
    // Функция для создания стилей тулбара
    const createToolbarStyle = useCallback((position, zIndex) => ({
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${position.width}px`,
        minWidth: `${position.width}px`,
        maxWidth: `${position.width}px`,
        display: 'flex',
        zIndex,
        padding: TOOLBAR_PADDING,
        borderRadius: TOOLBAR_BORDER_RADIUS,
        backgroundColor: TOOLBAR_BACKGROUND_COLOR,
        boxSizing: 'border-box',
        transform: `scale(${zoom})`,
        transformOrigin: 'left top',
    }), [zoom]);
    // Стили
    const toolbarStyle = useMemo(() => createToolbarStyle(toolbarPosition, TOOLBAR_Z_INDEX_BASE), [toolbarPosition, createToolbarStyle]);
    const tableToolbarStyle = useMemo(() => createToolbarStyle(tableToolbarPosition, TOOLBAR_Z_INDEX_TABLE), [tableToolbarPosition, createToolbarStyle]);
    const elementStyle = useMemo(() => ({
        position: renderMode ? 'relative' : 'absolute', // В режиме рендеринга используем relative
        left: renderMode ? 0 : `${elementPosition.x}px`,
        top: renderMode ? 0 : `${elementPosition.y}px`,
        width: `${elementPosition.width}px`,
        height: `${elementPosition.height}px`,
        padding: 0,
        zIndex: renderMode ? 0 : 1000, // В режиме рендеринга zIndex не важен
        backgroundColor: styles.backgroundColor || '#ffffff38',
        borderRadius: renderMode ? 0 : '8px', // В режиме рендеринга убираем скругление
        border: renderMode ? 'none' : '1px solid #076aff6e', // В режиме рендеринга убираем border
        boxSizing: 'border-box',
        touchAction: 'none',
    }), [elementPosition, styles.backgroundColor, renderMode]);
    const editorContainerStyle = {
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        padding: 0, // Убрали padding для единообразия с рендерером
        overflow: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
        touchAction: 'none',
    };
    const editorStyle = useMemo(() => ({
        fontSize: `${(styles.fontSize || DEFAULT_FONT_SIZE) * textScaleFromHeight * zoom}px`,
        fontFamily: styles.fontFamily || DEFAULT_FONT_FAMILY,
        color: styles.textColor || '#000000',
        textAlign: styles.textAlign || 'left',
        // Устанавливаем lineHeight как безразмерное значение для автоматического масштабирования
        lineHeight: 1.6,
        // Дополнительные свойства для предотвращения наложения строк
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'normal',
        verticalAlign: 'baseline',
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        minWidth: 0,
        minHeight: 0,
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        touchAction: 'none',
    }), [styles, textScaleFromHeight, zoom]);
    const buttonsStyle = useMemo(() => ({
        position: 'absolute',
        left: `${buttonsPosition.x}px`,
        top: `${buttonsPosition.y}px`,
        width: `${buttonsPosition.width}px`,
        height: `${buttonsPosition.height}px`,
        zIndex: 1001,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '8px',
        padding: '18px',
        boxSizing: 'border-box',
        transform: `scale(${zoom})`,
        transformOrigin: 'left top',
    }), [buttonsPosition, zoom]);
    const buttonStyle = {
        padding: '6px 12px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        background: '#fff',
        cursor: 'pointer',
        fontSize: '12px',
        color: '#333',
    };
    if (process.env.NODE_ENV === 'development') {
        console.count('editor render');
    }
    return (_jsxs(_Fragment, { children: [!renderMode && editorReady && (_jsxs(_Fragment, { children: [_jsx("div", { ref: toolbarRef, style: toolbarStyle, children: _jsx(TiptapToolbar, { editor: ((_f = editorRef.current) === null || _f === void 0 ? void 0 : _f.getEditor()) || null, onFontSizeChange: (size) => handleStyleChange({ fontSize: size }), onBackgroundColorChange: (color) => handleStyleChange({ backgroundColor: color }), onTextColorChange: (color) => handleStyleChange({ textColor: color }), onTextAlignChange: (align) => handleStyleChange({ textAlign: align }), showMarkdownSource: showMarkdownSource, onMarkdownSourceToggle: setShowMarkdownSource, currentFontSize: styles.fontSize, currentBackgroundColor: styles.backgroundColor, currentTextColor: styles.textColor, currentTextAlign: styles.textAlign, zoom: zoom, stagePosition: stagePosition }) }), _jsx("div", { ref: tableToolbarRef, style: tableToolbarStyle, children: _jsx(TableToolbar, { editor: ((_g = editorRef.current) === null || _g === void 0 ? void 0 : _g.getEditor()) || null }) })] })), _jsx("div", { ref: elementRef, style: elementStyle, onKeyDown: renderMode ? undefined : handleKeyDown, children: _jsx("div", { style: editorContainerStyle, children: _jsx(MemoizedEditorCore, { ref: editorRef, content: markdownContent, onUpdate: handleContentUpdate, showMarkdownSource: showMarkdownSource, placeholder: "\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u0442\u0435\u043A\u0441\u0442...", editable: !renderMode, style: editorStyle, className: "tiptap-text-editor" }) }) }), !renderMode && (_jsx("div", { ref: buttonsRef, style: buttonsStyle, children: _jsx("button", { type: "button", onClick: onCancel, style: buttonStyle, children: "\u041E\u0442\u043C\u0435\u043D\u0430 (Esc)" }) }))] }));
};
export default InlineTextEditor;
